import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../services/api';
import {
  DuplicateDonor,
  DuplicateGroup,
  DuplicateIndividual,
} from '../services/duplicateService';
import { useDebouncedValue } from '../hooks/useDebounce';

type Kind = 'client' | 'donor';

interface BaseProps {
  /**
   * If provided, pre-fills one slot with this record so the admin only needs
   * to pick the second one. Used from a record's detail page.
   */
  anchor?: DuplicateIndividual | DuplicateDonor;
  onCancel: () => void;
  onContinue: (group: DuplicateGroup<DuplicateIndividual> | DuplicateGroup<DuplicateDonor>) => void;
}

type Props =
  | (BaseProps & { kind: 'client' })
  | (BaseProps & { kind: 'donor' });

interface IndividualListItem {
  individualID: number;
  clientID?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  emailAddress?: string;
  zipCode?: string;
  city?: string;
  isActive?: boolean;
}

interface DonorListItem {
  donorID: number;
  fullName?: string;
  organizationCompany?: string;
  emailAddress?: string;
  phoneNumber?: string;
  donorType?: string;
  priorityLevel?: string;
  isActive?: boolean;
}

function recordId(kind: Kind, r: DuplicateIndividual | DuplicateDonor): number {
  return kind === 'client'
    ? (r as DuplicateIndividual).individualID
    : (r as DuplicateDonor).donorID;
}

function recordLabel(kind: Kind, r: DuplicateIndividual | DuplicateDonor): string {
  if (kind === 'client') {
    const c = r as DuplicateIndividual;
    return `${c.clientID || `#${c.individualID}`} · ${c.firstName || ''} ${c.lastName || ''}`.trim();
  }
  const d = r as DuplicateDonor;
  return `#${d.donorID} · ${d.fullName || '(no name)'}`;
}

function toDuplicateIndividual(row: IndividualListItem): DuplicateIndividual {
  return {
    individualID: row.individualID,
    clientID: row.clientID || null,
    firstName: row.firstName || null,
    lastName: row.lastName || null,
    dateOfBirth: row.dateOfBirth || null,
    gender: null,
    raceEthnicity: null,
    address: null,
    city: row.city || null,
    zipCode: row.zipCode || null,
    phoneNumber: row.phoneNumber || null,
    emailAddress: row.emailAddress || null,
    clientType: null,
    isActive: row.isActive ?? true,
  };
}

function toDuplicateDonor(row: DonorListItem): DuplicateDonor {
  return {
    donorID: row.donorID,
    fullName: row.fullName || null,
    salutation: null,
    organizationCompany: row.organizationCompany || null,
    emailAddress: row.emailAddress || null,
    phoneNumber: row.phoneNumber || null,
    mailingAddress: null,
    birthdayMonth: null,
    birthdayDay: null,
    donorType: row.donorType || null,
    priorityLevel: row.priorityLevel || null,
    preferredRecognition: null,
    isActive: row.isActive ?? true,
  };
}

export function ManualMergePicker(props: Props) {
  const { kind, anchor, onCancel, onContinue } = props;

  const [searchA, setSearchA] = useState('');
  const [searchB, setSearchB] = useState('');
  const debA = useDebouncedValue(searchA, 300);
  const debB = useDebouncedValue(searchB, 300);

  const [resultsA, setResultsA] = useState<(DuplicateIndividual | DuplicateDonor)[]>([]);
  const [resultsB, setResultsB] = useState<(DuplicateIndividual | DuplicateDonor)[]>([]);
  const [selectedA, setSelectedA] = useState<DuplicateIndividual | DuplicateDonor | null>(
    anchor ?? null
  );
  const [selectedB, setSelectedB] = useState<DuplicateIndividual | DuplicateDonor | null>(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [error, setError] = useState('');

  const showSlotA = !anchor;

  const runSearch = async (
    query: string,
    setLoading: (b: boolean) => void,
    setResults: (r: (DuplicateIndividual | DuplicateDonor)[]) => void
  ) => {
    setLoading(true);
    setError('');
    try {
      if (kind === 'client') {
        const resp = await apiGet<IndividualListItem[]>('/individuals', {
          searchQuery: query,
          page: 1,
          pageSize: 10,
          isActive: 'all',
        });
        if (resp.success) {
          setResults((resp.data || []).map(toDuplicateIndividual));
        } else {
          setError(resp.error?.message || 'Search failed');
          setResults([]);
        }
      } else {
        // Donors don't have server-side search yet — fetch a page and filter client-side.
        const resp = await apiGet<DonorListItem[]>('/donors');
        if (resp.success) {
          const term = query.toLowerCase();
          const filtered = (resp.data || []).filter((d) => {
            return (
              (d.fullName || '').toLowerCase().includes(term) ||
              (d.emailAddress || '').toLowerCase().includes(term) ||
              (d.phoneNumber || '').includes(query) ||
              (d.organizationCompany || '').toLowerCase().includes(term)
            );
          });
          setResults(filtered.slice(0, 10).map(toDuplicateDonor));
        } else {
          setError(resp.error?.message || 'Search failed');
          setResults([]);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showSlotA || !debA || debA.length < 2) {
      setResultsA([]);
      return;
    }
    runSearch(debA, setLoadingA, setResultsA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debA, kind, showSlotA]);

  useEffect(() => {
    if (!debB || debB.length < 2) {
      setResultsB([]);
      return;
    }
    runSearch(debB, setLoadingB, setResultsB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debB, kind]);

  const canContinue = useMemo(() => {
    if (!selectedA || !selectedB) {return false;}
    return recordId(kind, selectedA) !== recordId(kind, selectedB);
  }, [selectedA, selectedB, kind]);

  const handleContinue = () => {
    if (!selectedA || !selectedB) {return;}
    if (recordId(kind, selectedA) === recordId(kind, selectedB)) {
      setError('Pick two different records.');
      return;
    }
    // Build a synthetic group for the MergeDialog. Confidence is 0 because the
    // detector did not flag this pair — admin is doing this manually.
    const group = {
      confidence: 0,
      matchedRules: [
        {
          name: 'manual',
          label: 'Manual merge — not flagged by the duplicate scanner',
          confidence: 0,
        },
      ],
      members: [selectedA, selectedB],
    } as DuplicateGroup<DuplicateIndividual> | DuplicateGroup<DuplicateDonor>;
    onContinue(group);
  };

  const renderResults = (
    items: (DuplicateIndividual | DuplicateDonor)[],
    selected: DuplicateIndividual | DuplicateDonor | null,
    setSelected: (r: DuplicateIndividual | DuplicateDonor) => void,
    loading: boolean
  ) => {
    if (loading) {
      return <p style={{ color: '#666' }}>Searching…</p>;
    }
    if (items.length === 0) {
      return <p style={{ color: '#666' }}>Type 2+ characters to search.</p>;
    }
    return (
      <ul style={{ listStyle: 'none', padding: 0, maxHeight: '180px', overflowY: 'auto' }}>
        {items.map((r) => {
          const id = recordId(kind, r);
          const isSelected = selected && recordId(kind, selected) === id;
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => setSelected(r)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.4rem 0.6rem',
                  marginBottom: '0.25rem',
                  border: isSelected ? '2px solid #5B2C86' : '1px solid #ccc',
                  background: isSelected ? '#f4ecff' : 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {recordLabel(kind, r)}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Manually merge ${kind === 'client' ? 'clients' : 'donors'}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        className="card"
        style={{ background: 'white', maxWidth: '720px', width: '100%' }}
      >
        <h2 style={{ marginTop: 0 }}>
          Manually merge {kind === 'client' ? 'clients' : 'donors'}
        </h2>
        <p style={{ color: '#666' }}>
          Pick the two records that represent the same {kind === 'client' ? 'person' : 'donor'}.
          Choose carefully — the merge will move history from one record to the other.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label>{showSlotA ? 'Record A' : `Anchored: ${recordLabel(kind, anchor!)}`}</label>
            {showSlotA && (
              <>
                <input
                  type="text"
                  value={searchA}
                  onChange={(e) => setSearchA(e.target.value)}
                  placeholder="Search by name, email, phone…"
                  autoFocus
                />
                {renderResults(resultsA, selectedA, setSelectedA, loadingA)}
              </>
            )}
          </div>

          <div className="form-group">
            <label>Record B</label>
            <input
              type="text"
              value={searchB}
              onChange={(e) => setSearchB(e.target.value)}
              placeholder="Search by name, email, phone…"
              autoFocus={!showSlotA}
            />
            {renderResults(resultsB, selectedB, setSelectedB, loadingB)}
          </div>
        </div>

        {selectedA && selectedB && (
          <div className="alert" style={{ background: '#f4ecff' }}>
            <strong>Selected:</strong> {recordLabel(kind, selectedA)} &nbsp;⇄&nbsp;{' '}
            {recordLabel(kind, selectedB)}
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1rem',
          }}
        >
          <button className="button-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="button-primary" onClick={handleContinue} disabled={!canContinue}>
            Continue to merge
          </button>
        </div>
      </div>
    </div>
  );
}
